import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './QuizManage.css';

const QuizManage = () => {
  const { quizId, courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditing = !!quizId;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [courseName, setCourseName] = useState('');

  // Quiz metadata
  const [quizData, setQuizData] = useState({
    title: '',
    description: '',
    passing_score: 70,
    time_limit: 1800, // 30 minutes in seconds
    max_attempts: 3,
    randomize_questions: false,
    show_correct_answers: true,
    certificate_on_pass: false,
    course_id: courseId || null
  });

  // Questions array
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch course name if creating new quiz
        if (courseId && !isEditing) {
          const courseRes = await api.get(`/courses/${courseId}`);
          setCourseName(courseRes.data.title);
          setQuizData(prev => ({ ...prev, course_id: parseInt(courseId) }));
        }

        // Fetch quiz data if editing
        if (isEditing) {
          const quizRes = await api.get(`/quizzes/${quizId}/full`);
          const quiz = quizRes.data;

          setQuizData({
            title: quiz.title || '',
            description: quiz.description || '',
            passing_score: quiz.passing_score || 70,
            time_limit: quiz.time_limit || 1800,
            max_attempts: quiz.max_attempts || 3,
            randomize_questions: quiz.randomize_questions || false,
            show_correct_answers: quiz.show_correct_answers !== false,
            certificate_on_pass: quiz.certificate_on_pass || false,
            course_id: quiz.course_id
          });

          // Map questions
          if (quiz.questions && quiz.questions.length > 0) {
            setQuestions(quiz.questions.map(q => ({
              id: q.id,
              question_type: q.question_type || 'multiple_choice',
              question_text: q.question_text || '',
              options: q.options || ['', '', '', ''],
              correct_answer: q.correct_answer || '',
              explanation: q.explanation || '',
              points: q.points || 1
            })));
          }

          // Fetch course name
          if (quiz.course_id) {
            try {
              const courseRes = await api.get(`/courses/${quiz.course_id}`);
              setCourseName(courseRes.data.title);
            } catch (err) {
              console.error('Error fetching course:', err);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching quiz data:', err);
        setError('Failed to load quiz data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [quizId, courseId, isEditing]);

  const handleQuizChange = (field, value) => {
    setQuizData(prev => ({ ...prev, [field]: value }));
  };

  const addQuestion = () => {
    setQuestions(prev => [...prev, {
      id: null,
      question_type: 'multiple_choice',
      question_text: '',
      options: ['', '', '', ''],
      correct_answer: '',
      explanation: '',
      points: 1
    }]);
  };

  const removeQuestion = (index) => {
    setQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const updateQuestion = (index, field, value) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== index) return q;

      // Handle special cases
      if (field === 'question_type' && value === 'true_false') {
        return { ...q, [field]: value, options: ['True', 'False'], correct_answer: '' };
      }
      if (field === 'question_type' && value === 'multiple_choice') {
        return { ...q, [field]: value, options: ['', '', '', ''], correct_answer: '' };
      }

      return { ...q, [field]: value };
    }));
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== questionIndex) return q;
      const newOptions = [...q.options];
      newOptions[optionIndex] = value;
      return { ...q, options: newOptions };
    }));
  };

  const handleSave = async () => {
    // Validation
    if (!quizData.title.trim()) {
      setError('Quiz title is required');
      return;
    }

    if (questions.length === 0) {
      setError('At least one question is required');
      return;
    }

    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question_text.trim()) {
        setError(`Question ${i + 1} text is required`);
        return;
      }
      if (!q.correct_answer) {
        setError(`Question ${i + 1} needs a correct answer selected`);
        return;
      }
      if (q.question_type === 'multiple_choice') {
        const filledOptions = q.options.filter(opt => opt.trim());
        if (filledOptions.length < 2) {
          setError(`Question ${i + 1} needs at least 2 options`);
          return;
        }
      }
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        ...quizData,
        questions: questions.map(q => ({
          ...q,
          options: q.question_type === 'multiple_choice'
            ? q.options.filter(opt => opt.trim())
            : q.options
        }))
      };

      if (isEditing) {
        await api.put(`/quizzes/${quizId}`, payload);
      } else {
        await api.post('/quizzes', payload);
      }

      // Navigate back to course
      if (quizData.course_id) {
        navigate(`/courses/${quizData.course_id}`);
      } else {
        navigate(-1);
      }
    } catch (err) {
      console.error('Error saving quiz:', err);
      setError(err.response?.data?.message || 'Failed to save quiz');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const formatTimeLimit = (seconds) => {
    const mins = Math.floor(seconds / 60);
    return `${mins} minute${mins !== 1 ? 's' : ''}`;
  };

  return (
    <div className="page quiz-manage-page">
      <div className="container">
        <section className="quiz-manage-hero">
          <div className="hero-content">
            <p className="eyebrow">Assessment</p>
            <h1>{isEditing ? 'Edit Quiz' : 'Create New Quiz'}</h1>
            {courseName && <p>For course: {courseName}</p>}
          </div>
          <div className="hero-actions">
            <button
              className="btn-secondary"
              onClick={() => navigate(-1)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : (isEditing ? 'Update Quiz' : 'Create Quiz')}
            </button>
          </div>
        </section>

        {error && <div className="error-message">{error}</div>}

        {/* Quiz Metadata Section */}
        <section className="quiz-metadata-section">
          <h2>Quiz Settings</h2>
          <div className="metadata-grid">
            <div className="form-group full-width">
              <label>Quiz Title *</label>
              <input
                type="text"
                value={quizData.title}
                onChange={(e) => handleQuizChange('title', e.target.value)}
                placeholder="Enter quiz title"
              />
            </div>

            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                value={quizData.description}
                onChange={(e) => handleQuizChange('description', e.target.value)}
                placeholder="Enter quiz description"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Passing Score (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={quizData.passing_score}
                onChange={(e) => handleQuizChange('passing_score', parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="form-group">
              <label>Time Limit</label>
              <select
                value={quizData.time_limit}
                onChange={(e) => handleQuizChange('time_limit', parseInt(e.target.value))}
              >
                <option value={300}>5 minutes</option>
                <option value={600}>10 minutes</option>
                <option value={900}>15 minutes</option>
                <option value={1200}>20 minutes</option>
                <option value={1800}>30 minutes</option>
                <option value={2700}>45 minutes</option>
                <option value={3600}>1 hour</option>
                <option value={5400}>1.5 hours</option>
                <option value={7200}>2 hours</option>
                <option value={0}>No limit</option>
              </select>
              <span className="field-hint">
                {quizData.time_limit > 0 ? formatTimeLimit(quizData.time_limit) : 'No time limit'}
              </span>
            </div>

            <div className="form-group">
              <label>Max Attempts</label>
              <input
                type="number"
                min="1"
                max="100"
                value={quizData.max_attempts}
                onChange={(e) => handleQuizChange('max_attempts', parseInt(e.target.value) || 1)}
              />
            </div>
          </div>

          <div className="toggles-section">
            <label className="toggle-item">
              <input
                type="checkbox"
                checked={quizData.randomize_questions}
                onChange={(e) => handleQuizChange('randomize_questions', e.target.checked)}
              />
              <span className="toggle-label">Randomize Questions</span>
              <span className="toggle-hint">Shuffle question order for each attempt</span>
            </label>

            <label className="toggle-item">
              <input
                type="checkbox"
                checked={quizData.show_correct_answers}
                onChange={(e) => handleQuizChange('show_correct_answers', e.target.checked)}
              />
              <span className="toggle-label">Show Correct Answers</span>
              <span className="toggle-hint">Display correct answers after submission</span>
            </label>

            <label className="toggle-item">
              <input
                type="checkbox"
                checked={quizData.certificate_on_pass}
                onChange={(e) => handleQuizChange('certificate_on_pass', e.target.checked)}
              />
              <span className="toggle-label">Certificate on Pass</span>
              <span className="toggle-hint">Award certificate when student passes</span>
            </label>
          </div>
        </section>

        {/* Questions Section */}
        <section className="questions-section">
          <div className="section-header">
            <h2>Questions ({questions.length})</h2>
            <button className="btn-secondary add-question-btn" onClick={addQuestion}>
              + Add Question
            </button>
          </div>

          {questions.length === 0 ? (
            <div className="empty-questions">
              <p>No questions added yet.</p>
              <p>Click "Add Question" to create your first question.</p>
            </div>
          ) : (
            <div className="questions-list">
              {questions.map((question, qIndex) => (
                <div key={qIndex} className="question-card">
                  <div className="question-header">
                    <span className="question-number">Question {qIndex + 1}</span>
                    <div className="question-controls">
                      <select
                        value={question.question_type}
                        onChange={(e) => updateQuestion(qIndex, 'question_type', e.target.value)}
                        className="type-select"
                      >
                        <option value="multiple_choice">Multiple Choice</option>
                        <option value="true_false">True/False</option>
                      </select>
                      <button
                        className="remove-btn"
                        onClick={() => removeQuestion(qIndex)}
                        title="Remove question"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <div className="question-body">
                    <div className="form-group">
                      <label>Question Text *</label>
                      <textarea
                        value={question.question_text}
                        onChange={(e) => updateQuestion(qIndex, 'question_text', e.target.value)}
                        placeholder="Enter your question"
                        rows={2}
                      />
                    </div>

                    {question.question_type === 'multiple_choice' ? (
                      <div className="options-section">
                        <label>Options (select correct answer)</label>
                        <div className="options-list">
                          {question.options.map((option, oIndex) => (
                            <div key={oIndex} className="option-row">
                              <input
                                type="radio"
                                name={`correct-${qIndex}`}
                                checked={question.correct_answer === String.fromCharCode(65 + oIndex)}
                                onChange={() => updateQuestion(qIndex, 'correct_answer', String.fromCharCode(65 + oIndex))}
                              />
                              <span className="option-label">{String.fromCharCode(65 + oIndex)}.</span>
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                                placeholder={`Option ${String.fromCharCode(65 + oIndex)}`}
                                className="option-input"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="options-section true-false">
                        <label>Correct Answer</label>
                        <div className="true-false-options">
                          <label className="tf-option">
                            <input
                              type="radio"
                              name={`correct-${qIndex}`}
                              checked={question.correct_answer === 'True'}
                              onChange={() => updateQuestion(qIndex, 'correct_answer', 'True')}
                            />
                            <span>True</span>
                          </label>
                          <label className="tf-option">
                            <input
                              type="radio"
                              name={`correct-${qIndex}`}
                              checked={question.correct_answer === 'False'}
                              onChange={() => updateQuestion(qIndex, 'correct_answer', 'False')}
                            />
                            <span>False</span>
                          </label>
                        </div>
                      </div>
                    )}

                    <div className="question-extras">
                      <div className="form-group">
                        <label>Explanation (shown after answer)</label>
                        <textarea
                          value={question.explanation}
                          onChange={(e) => updateQuestion(qIndex, 'explanation', e.target.value)}
                          placeholder="Explain the correct answer"
                          rows={2}
                        />
                      </div>

                      <div className="form-group points-group">
                        <label>Points</label>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={question.points}
                          onChange={(e) => updateQuestion(qIndex, 'points', parseInt(e.target.value) || 1)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {questions.length > 0 && (
            <div className="questions-footer">
              <button className="btn-secondary add-question-btn" onClick={addQuestion}>
                + Add Another Question
              </button>
              <p className="total-points">
                Total Points: {questions.reduce((sum, q) => sum + (q.points || 1), 0)}
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default QuizManage;
